import { warehouseApi as api, apiError } from '../shared/warehouseApi';
import { ScanResult } from './ScanResult';
import { useState, useEffect, useRef } from 'react';
import QrScanner from 'react-qr-scanner';
import { X, QrCode, Keyboard, CheckCircle, Loader2 } from 'lucide-react';

/**
 * ScannerModal
 *
 * Modal overlay for admin scans (QR + manual keypad).
 * On successful scan, calls onSuccess() so the parent can refresh the table,
 * then stays open for the next scan — allowing rapid warehouse scanning.
 * The admin closes it manually via the X button.
 */
export function ScannerModal({ userId, onClose, onSuccess }) {
  const [mode, setMode] = useState('menu');       // 'menu' | 'qr' | 'manual'
  const [manualId, setManualId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [backCameraId, setBackCameraId] = useState(null);
  const successTimerRef = useRef(null);
  const scanInFlightRef = useRef(false);

  // Detect rear camera on mount
  useEffect(() => {
    navigator.mediaDevices
      ?.getUserMedia({ video: true })
      .then(() => navigator.mediaDevices.enumerateDevices())
      .then(devices => {
        const rear = devices.find(d =>
          d.kind === 'videoinput' && /rear|back|environment/i.test(d.label)
        );
        setBackCameraId(rear?.deviceId || null);
      })
      .catch(() => {});
  }, []);

  // Allow time to read the zone/drawer instruction before the next scan.
  useEffect(() => {
    if (!lastResult) return;
    successTimerRef.current = setTimeout(() => {
      setLastResult(null);
      setMode('menu');
    }, 6000);
    return () => clearTimeout(successTimerRef.current);
  }, [lastResult]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const updateStatus = async (identifier) => {
    if (scanInFlightRef.current) return;
    scanInFlightRef.current = true;
    setScanning(true);
    setError(null);

    try {
      const { data } = await api.post(`/api/admin/packages/${encodeURIComponent(identifier)}/scan`, {});
      setLastResult(data);
      onSuccess(); // trigger parent refresh
      setMode('success');
    } catch (err) {
      setError(apiError(err));
      setMode('menu');
    } finally {
      scanInFlightRef.current = false;
      setScanning(false);
    }
  };

  const handleQrScan = (result) => {
    if (!result || scanning) return;
    const code = typeof result === 'string' ? result : result?.text;
    if (!code) return;
    updateStatus(code);
  };

  const handleManualSubmit = () => {
    if (!manualId.trim()) return;
    const id = manualId.trim();
    setManualId('');
    updateStatus(id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 bg-brand-primary">
          <QrCode size={20} className="text-white" />
          <span className="font-montserrat font-extrabold text-white text-lg flex-1">
            Escanear Paquete
          </span>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">

          {/* Error banner */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm font-montserrat text-red-600">
              {error}
              <button
                onClick={() => setError(null)}
                className="ml-2 underline"
              >
                Cerrar
              </button>
            </div>
          )}

          {/* Success state */}
          {mode === 'success' && lastResult && (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle size={28} className="text-brand-green" />
              </div>
              <p className="font-montserrat font-bold text-brand-green text-lg text-center">
                Paquete escaneado
              </p>
              <div className="bg-gray-50 rounded-xl p-4 w-full">
                <ScanResult result={lastResult} />
              </div>
              <p className="text-xs text-gray-400 font-montserrat">
                Listo para el siguiente escaneo...
              </p>
            </div>
          )}

          {/* Menu: QR or manual */}
          {mode === 'menu' && (
            <div className="space-y-3">
              <button
                onClick={() => setMode('qr')}
                className="w-full flex items-center justify-center gap-3 bg-brand-primary hover:bg-brand-primary/90 text-white font-montserrat font-semibold py-5 rounded-xl transition-colors"
              >
                <QrCode size={24} />
                <span>Escanear QR</span>
              </button>
              <button
                onClick={() => setMode('manual')}
                className="w-full flex items-center justify-center gap-3 bg-brand-green hover:bg-brand-green/90 text-white font-montserrat font-semibold py-5 rounded-xl transition-colors"
              >
                <Keyboard size={24} />
                <span>Código Manual</span>
              </button>
            </div>
          )}

          {/* QR scanner */}
          {mode === 'qr' && (
            <div className="space-y-3">
              {scanning ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 size={28} className="animate-spin text-brand-primary" />
                  <p className="font-montserrat text-sm text-gray-500">Procesando...</p>
                </div>
              ) : (
                <div className="rounded-xl overflow-hidden bg-black">
                  <QrScanner
                    delay={300}
                    onError={e => console.error('QR error:', e)}
                    onScan={handleQrScan}
                    style={{ width: '100%' }}
                    constraints={{
                      video: backCameraId
                        ? { deviceId: { exact: backCameraId } }
                        : { facingMode: { ideal: 'environment' } }
                    }}
                  />
                </div>
              )}
              <button
                onClick={() => setMode('menu')}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-montserrat font-semibold rounded-xl transition-colors"
              >
                Cancelar
              </button>
            </div>
          )}

          {/* Manual keypad */}
          {mode === 'manual' && (
            <div className="space-y-3">
              {/* Display */}
              <div className="bg-gray-100 border-2 border-brand-primary rounded-xl p-5 min-h-[72px] flex items-center justify-center">
                {manualId ? (
                  <span className="font-montserrat font-bold text-3xl text-gray-800 tracking-widest">
                    {manualId}
                  </span>
                ) : (
                  <span className="font-montserrat text-gray-400">Ingrese el código</span>
                )}
              </div>

              {/* Keypad */}
              <div className="grid grid-cols-3 gap-2">
                {[1,2,3,4,5,6,7,8,9].map(n => (
                  <button
                    key={n}
                    onClick={() => setManualId(p => p + n)}
                    className="bg-gray-100 hover:bg-brand-primary/10 active:bg-brand-primary/20 text-gray-800 font-montserrat font-bold text-xl py-5 rounded-xl transition-colors border-2 border-transparent hover:border-brand-primary"
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setManualId('')}
                  className="bg-red-100 hover:bg-red-200 text-red-700 font-montserrat font-semibold py-5 rounded-xl transition-colors border-2 border-transparent"
                >
                  Borrar
                </button>
                <button
                  onClick={() => setManualId(p => p + '0')}
                  className="bg-gray-100 hover:bg-brand-primary/10 text-gray-800 font-montserrat font-bold text-xl py-5 rounded-xl transition-colors border-2 border-transparent hover:border-brand-primary"
                >
                  0
                </button>
                <button
                  onClick={() => setManualId(p => p.slice(0, -1))}
                  className="bg-orange-100 hover:bg-orange-200 text-orange-700 font-montserrat font-semibold py-5 rounded-xl transition-colors border-2 border-transparent"
                >
                  ←
                </button>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleManualSubmit}
                  disabled={!manualId || scanning}
                  className={`flex-1 py-4 font-montserrat font-bold rounded-xl transition-colors flex items-center justify-center gap-2 ${
                    manualId && !scanning
                      ? 'bg-brand-primary text-white hover:bg-brand-primary/90'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {scanning && <Loader2 size={16} className="animate-spin" />}
                  Confirmar
                </button>
                <button
                  onClick={() => { setMode('menu'); setManualId(''); }}
                  className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-montserrat font-semibold rounded-xl transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
